import { ref, computed, watch } from 'vue'
import { reverseGeocode } from '../api/heweather.js'
import { locale, tp } from '../i18n/index.js'

const KEY = 'OPERA.weather.location'

/** Data source selector — mirrors weatherApi SOURCE without creating an import cycle. */
const SOURCE = import.meta.env.VITE_WEATHER_SOURCE || 'mock'

/**
 * Transient placeholder used ONLY while the current city is still being
 * resolved (or as a last resort if resolution fails). It deliberately carries NO
 * real city (no "北京市 海淀区" default) and is never shown as a city name nor
 * kept in the saved list — the app resolves a real city from the coordinates.
 */
const PLACEHOLDER_LOCATION = {
  id: 'geo',
  name: '当前位置',
  region: '',
  country: 'CN',
  lat: 0,
  lon: 0,
  type: 'geo',
  src: 'placeholder'
}

/** The neutral placeholder (never a real city) — used to keep it out of the list. */
const isPlaceholder = (loc) => !!loc && loc.id === 'geo'

/** True only when a place carries real front-end coordinates (0,0 = "unknown"). */
function hasRealCoords(loc) {
  if (!loc) return false
  const lat = Number(loc.lat)
  const lon = Number(loc.lon)
  return Number.isFinite(lat) && Number.isFinite(lon) && !(lat === 0 && lon === 0)
}

/** Normalise a place label for identity comparison ("北京市" ≡ "北京"). */
const normLabel = (s) => String(s || '').trim().toLowerCase().replace(/[市省区县]$/, '')

/** Coordinates within ~0.08° (≈ 9 km) count as the same place for weather. */
const NEAR_DEG = 0.08

/**
 * Are two locations the SAME place?
 *
 * Dedup must NOT rely on `id` alone: one city arrives under different ids
 * depending on the entry point — the auto-detected current city is the *district*
 * nearest the coords (e.g. 金水/郑州, id 101180112) while a search pick is the
 * *city* (郑州/河南省, id 101180101). Same place, different ids ⇒ two rows.
 *
 * Match on: identical id, near-identical coordinates (when both have real
 * coords), or an identical label (fallback for entries without usable coords).
 * Coordinates take priority so two same-named places far apart stay separate.
 */
function isSamePlace(a, b) {
  if (!a || !b) return false
  if (String(a.id) === String(b.id)) return true
  const bothCoords = hasRealCoords(a) && hasRealCoords(b)
  if (bothCoords) {
    const d = Math.hypot(Number(a.lat) - Number(b.lat), Number(a.lon) - Number(b.lon))
    if (d < NEAR_DEG) return true
  }
  if (!bothCoords) {
    if (a.nameEn && b.nameEn &&
        normLabel(a.nameEn) === normLabel(b.nameEn) &&
        normLabel(a.regionEn) === normLabel(b.regionEn)) return true
    if (normLabel(a.name) && normLabel(a.name) === normLabel(b.name) &&
        normLabel(a.region) === normLabel(b.region)) return true
  }
  return false
}

/**
 * Collapse a list of places into unique ones (first occurrence wins). An explicit
 * user pick (`src: 'user'`) outranks an auto-detected entry for the same place.
 */
function dedupePlaces(list) {
  const out = []
  for (const l of list) {
    if (!l || isPlaceholder(l)) continue
    const i = out.findIndex((x) => isSamePlace(x, l))
    if (i === -1) out.push(l)
    else if (out[i].src !== 'user' && l.src === 'user') out[i] = { ...out[i], ...l }
  }
  return out
}

/**
 * Stamp provenance on legacy entries that predate the `src` marker. An inherited
 * `'placeholder'` value (from the merge above) counts as "unset".
 */
const stampSrc = (l) => {
  const src = l.src && l.src !== 'placeholder' ? l.src : (l.type === 'geo' ? 'device' : 'user')
  return { ...l, src }
}

function read() {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return PLACEHOLDER_LOCATION
    const parsed = { ...PLACEHOLDER_LOCATION, ...JSON.parse(raw) }
    // id 'geo' is the neutral placeholder — it has no real city, so never let a
    // reverse-geocoded English label (or anything else) stick to it.
    return parsed.id === 'geo' ? PLACEHOLDER_LOCATION : stampSrc(parsed)
  } catch {
    return PLACEHOLDER_LOCATION
  }
}

/** Read the user's saved cities from localStorage (falls back to the default). */
function readSaved() {
  try {
    const raw = localStorage.getItem(KEY + 's')
    if (raw) {
      const arr = JSON.parse(raw)
      if (Array.isArray(arr) && arr.length) {
        // The neutral placeholder must never sit in the city list, and legacy
        // duplicates (same place saved under different ids) are collapsed here.
        const cleaned = dedupePlaces(
          arr
            .filter((l) => l && !isPlaceholder(l))
            .map((l) => stampSrc({ ...PLACEHOLDER_LOCATION, ...l }))
        )
        // Write back when we dropped anything, so the stored list matches the UI.
        if (cleaned.length !== arr.length) {
          localStorage.setItem(KEY + 's', JSON.stringify(cleaned))
        }
        return cleaned
      }
    }
  } catch { /* ignore */ }
  // No stored cities: start empty (no default-city seeding).
  return []
}

/**
 * Ensure a location carries English name/region (nameEn/regionEn) so it renders
 * correctly in the English UI. We reverse-geocode its lat/lon (the API returns
 * English labels for lang=en); the result is cached on the object as
 * nameEn/regionEn so each place is only looked up once. Falls back to the
 * original (Chinese) labels if the lookup fails or when there are no real coords
 * (the neutral placeholder must never acquire an address from "0,0").
 */
async function ensureEnglish(loc) {
  if (!loc) return loc
  if (loc.nameEn && loc.regionEn) return loc
  if (!hasRealCoords(loc)) return loc
  try {
    const en = await reverseGeocode(loc.lat, loc.lon, 'en-US')
    if (en) return { ...loc, nameEn: en.name, regionEn: en.region }
  } catch { /* keep Chinese */ }
  return loc
}

/** Add a city to the saved list (dedup by place, not just id), persisting the change. */
async function addSaved(loc) {
  if (!loc || isPlaceholder(loc)) return
  const withEn = await ensureEnglish(loc)
  const idx = saved.value.findIndex((l) => isSamePlace(l, withEn))
  if (idx === -1) {
    saved.value = [withEn, ...saved.value]
  } else {
    // Merge in place. A user pick outranks an auto-detected entry for the same
    // place, so choosing 郑州 from search replaces the auto-detected 金水 row
    // instead of stacking a second one next to it.
    const prev = saved.value[idx]
    const merged = prev.src === 'user' && withEn.src !== 'user'
      ? { ...withEn, ...prev }
      : { ...prev, ...withEn }
    saved.value = saved.value.map((l, i) => (i === idx ? merged : l))
    // Keep the active slot pointing at the surviving entry (e.g. the user's city
    // label won over the auto-detected district).
    const a = active.value
    if (!isPlaceholder(a) && isSamePlace(a, withEn) && merged.id !== a.id) {
      active.value = merged
      persist(active.value)
    }
  }
  persistSaved(saved.value)
}

/**
 * One-time cleanup of location data written by earlier builds.
 *
 * Two kinds of "默认数据" used to leak into the UI:
 *   - a hardcoded default city ("北京市 海淀区", id 101010200) shipped in the code;
 *   - an IP-geolocation auto-fill that surfaced a server-side city (e.g. 郑州).
 * Both looked like a default the user never picked. Locations saved before this
 * change carry no `src` provenance marker, so we drop them and start neutral;
 * going forward every saved place is stamped `src: 'user' | 'device'`.
 */
function purgeLegacyDefaults() {
  const legacy = (l) => {
    if (!l) return false
    if (isPlaceholder(l)) return true                    // "当前位置" pseudo-city
    if (String(l.id) === '101010200') return true        // old hardcoded Beijing/Haidian
    if (l.name === '海淀区' || l.region === '北京市') return true
    // pre-marker auto-detection (IP geolocation / GPS) — re-resolved on next load.
    // User-search picks are type 'city' and are kept.
    if (l.src == null && (l.type === 'geo' || l.type === 'Place')) return true
    return false
  }
  const sweep = (raw, single) => {
    if (!raw) return
    const parsed = JSON.parse(raw)
    const arr = Array.isArray(parsed) ? parsed : [parsed]
    const kept = arr.filter((l) => !legacy(l))
    if (kept.length === arr.length) return // nothing to purge
    if (single) {
      if (kept.length) localStorage.setItem(KEY, JSON.stringify(kept[0]))
      else localStorage.removeItem(KEY)
    } else if (kept.length) {
      localStorage.setItem(KEY + 's', JSON.stringify(kept))
    } else {
      localStorage.removeItem(KEY + 's')
    }
  }
  try {
    sweep(localStorage.getItem(KEY), true)
    sweep(localStorage.getItem(KEY + 's'), false)
    // pre-rename keys from the MSN-era build
    localStorage.removeItem('msn.weather.location')
    localStorage.removeItem('msn.weather.locations')
  } catch { /* ignore */ }
}
purgeLegacyDefaults()

const active = ref(read())
const saved = ref(readSaved())
// 把当前城市放进「我的城市」列表用于首页切换条——但中性占位（"当前位置"）不进列表。
if (!isPlaceholder(active.value)) {
  const idx = saved.value.findIndex((l) => isSamePlace(l, active.value))
  if (idx === -1) {
    saved.value = [active.value, ...saved.value]
    persistSaved(saved.value)
  } else if (saved.value[idx].id !== active.value.id) {
    // Legacy data: the active city and its list row are the same place stored
    // under two ids. Unify them so the switcher highlights the right row.
    const entry = saved.value[idx]
    if (active.value.src === 'user' || entry.src !== 'user') {
      saved.value = saved.value.map((l, i) => (i === idx ? { ...l, ...active.value } : l))
      persistSaved(saved.value)
    } else {
      active.value = entry
      persist(active.value)
    }
  }
}
const locating = ref(false)
const currentCity = ref(null) // auto-detected real current city
const detectedApplied = ref(false) // is the active location the auto-detected one?
let detectStarted = false // run the initial resolution at most once per page load
/**
 * False while the current city is still being resolved from coordinates, so the
 * weather request is not fired with locHandle "0,0" (the gateway rejects it with
 * HTTP 400 "invalid locHandle"). Mock mode needs no resolution, so it starts ready.
 */
const locationReady = ref(SOURCE !== 'heweather')

function persist(loc) {
  try { localStorage.setItem(KEY, JSON.stringify(loc)) } catch { /* noop */ }
}

function persistSaved(list) {
  try { localStorage.setItem(KEY + 's', JSON.stringify(list)) } catch { /* noop */ }
}

/**
 * Try the device's GPS location (accurate). Resolves to {lat,lon} or null if
 * unsupported, denied, or it times out. Never throws — callers fall back to
 * "0,0", which the gateway resolves to the caller's own city.
 */
function getDeviceCoords(timeout = 6000) {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) return resolve(null)
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: false, timeout, maximumAge: 300000 }
    )
  })
}

/**
 * Resolve the user's CURRENT city on entry (live mode only).
 *
 * Coordinates: prefer the device's GPS (真实前端经纬度); when the device cannot
 * supply them we use "0,0" — the gateway resolves `location=0,0` to the caller's
 * own city, so this still yields a real place instead of an invented default.
 *
 * The coordinates are then reverse-geocoded into a REAL city (name + region +
 * its own lat/lon), which becomes the active location and is used for every
 * weather call. `"当前位置"` is only a transient placeholder and is never shown
 * as the city name nor kept in the city list.
 *
 * A stored user choice (saved city or explicit pick) always wins.
 */
async function detectCurrentCity() {
  if (SOURCE !== 'heweather') { locationReady.value = true; return }
  // A stored user choice always wins; only auto-fill when nothing was chosen yet,
  // or when the active location is the one we previously auto-detected (so a
  // language switch can re-translate it). Never override an explicit user pick.
  if (localStorage.getItem(KEY) && !detectedApplied.value) { locationReady.value = true; return }
  try {
    // Prefer GPS, but cap the wait at 3s so a pending permission prompt cannot
    // stall the first paint; then fall back to "0,0".
    const geo = (await getDeviceCoords(3000)) || { lat: 0, lon: 0 }
    const loc = await reverseGeocode(geo.lat, geo.lon, locale.value)
    if (loc) {
      currentCity.value = loc
      const enriched = await ensureEnglish({ ...loc, type: 'geo', src: 'device' })
      active.value = enriched
      persist(active.value)
      // keep the resolved real city in the switcher list (never a placeholder)
      await addSaved(enriched)
      detectedApplied.value = true
    }
  } catch (e) {
    console.warn('[location] current city resolution failed:', e)
  } finally {
    // Unblock the weather load even on failure (it then surfaces a retryable error
    // rather than hanging on a skeleton).
    locationReady.value = true
  }
}

/**
 * Holds the currently selected place plus the user's saved places.
 * OPERA encodes the selection into the `loc` query param as base64 JSON; this
 * composable mirrors that shape so the two stay interoperable.
 */
/**
 * Returns the city name + region for the current UI language. In English mode it
 * prefers the stored English fields (nameEn/regionEn) that were backfilled when
 * the place was saved; otherwise it falls back to the Chinese labels.
 */
function displayLoc(loc) {
  if (!loc) return { name: '', region: '' }
  const en = locale.value !== 'zh-CN'
  // Prefer the reverse-geocoded English label; otherwise translate the Chinese
  // one via the phrase table (tp is identity in zh-CN, so ZH is unaffected).
  // This is what renders the neutral placeholder as "Current location" in EN.
  return {
    name: tp(en && loc.nameEn ? loc.nameEn : (loc.name || '')),
    region: tp(en && loc.regionEn ? loc.regionEn : (loc.region || ''))
  }
}

/** One-time backfill: add English labels to any already-saved places that lack them. */
async function backfillSavedEnglish() {
  // Skip places without real coordinates (e.g. the neutral placeholder): looking
  // up "0,0" would attach a bogus English address to a place that has no city.
  if (!saved.value.some((l) => !(l.nameEn && l.regionEn) && hasRealCoords(l))) return
  const updated = await Promise.all(saved.value.map(async (l) => {
    if (l.nameEn && l.regionEn) return l
    if (!hasRealCoords(l)) return l
    try {
      const en = await reverseGeocode(l.lat, l.lon, 'en-US')
      if (en) return { ...l, nameEn: en.name, regionEn: en.region }
    } catch { /* ignore */ }
    return l
  }))
  saved.value = updated
  persistSaved(saved.value)
  const a = active.value
  const patched = updated.find((l) => l.id === a.id)
  if (patched && patched.nameEn && patched.nameEn !== a.nameEn) {
    active.value = { ...a, nameEn: patched.nameEn, regionEn: patched.regionEn }
    persist(active.value)
  }
}

export function useLocation() {
  const activeLocation = computed(() => active.value)
  const savedLocations = computed(() => saved.value)

  async function setLocation(loc) {
    const withEn = await ensureEnglish({ ...loc, src: loc.src || 'user' })
    active.value = { ...withEn }
    detectedApplied.value = false
    persist(active.value)
    // keep the saved list in sync so the English label is available everywhere
    // (match by place, so picking a city already stored under a district id
    // updates that row instead of adding a duplicate one)
    const idx = saved.value.findIndex((l) => isSamePlace(l, withEn))
    if (idx === -1) {
      saved.value = [withEn, ...saved.value]
    } else {
      saved.value = saved.value.map((l, i) => (i === idx ? { ...l, ...withEn } : l))
    }
    persistSaved(saved.value)
  }

  async function useDeviceLocation() {
    locating.value = true
    try {
      const pos = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) return reject(new Error('unsupported'))
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 6000,
          maximumAge: 300000
        })
      })
      const { latitude, longitude } = pos.coords
      // Reverse-geocode the GPS coords so we show a real city name
      // (e.g. "大同市") instead of the bare "当前位置". Adopting the resolved
      // city's own id/coords also makes this entry de-dupe cleanly against the
      // same city added from search (or resolved automatically on entry).
      let name = '当前位置'
      let region = ''
      let id = `${latitude.toFixed(4)},${longitude.toFixed(4)}`
      let lat = latitude
      let lon = longitude
      try {
        const rev = await reverseGeocode(latitude, longitude, locale.value)
        if (rev) {
          id = rev.id || id
          name = rev.name
          region = rev.region
          if (hasRealCoords(rev)) { lat = rev.lat; lon = rev.lon }
        }
      } catch { /* keep "当前位置" as last resort */ }
      const enriched = await ensureEnglish({
        id,
        name,
        region,
        country: 'CN',
        lat,
        lon,
        type: 'geo',
        src: 'device'
      })
      active.value = enriched
      detectedApplied.value = false
      locationReady.value = true
      persist(active.value)
      // Only list a real city — "当前位置" must never sit in the city list.
      if (name !== '当前位置') await addSaved(enriched)
      return active.value
    } finally {
      locating.value = false
    }
  }

  function saveCurrentLocation() {
    if (isPlaceholder(active.value)) return
    if (!saved.value.some((l) => isSamePlace(l, active.value))) {
      saved.value = [active.value, ...saved.value]
      persistSaved(saved.value)
    }
  }

  function removeLocation(id) {
    saved.value = saved.value.filter((l) => l.id !== id)
    persistSaved(saved.value)
  }

  /** base64 JSON — same encoding OPERA puts in `?loc=`. */
  function toLocParam(loc = active.value) {
    const payload = {
      i: loc.name, r: loc.region, c: '中华人民共和国',
      i2: 'cn', g: 'zh-cn',
      x: String(loc.lon), y: String(loc.lat)
    }
    try { return btoa(unescape(encodeURIComponent(JSON.stringify(payload)))) } catch { return '' }
  }

  // Resolve the real current city once on first load (non-blocking), regardless
  // of how many components call useLocation().
  if (!detectStarted) { detectStarted = true; detectCurrentCity() }

  return { activeLocation, savedLocations, locating, currentCity, locationReady, setLocation, useDeviceLocation, saveCurrentLocation, addSaved, removeLocation, toLocParam, displayLoc }
}

// Re-detect (re-translate) the current city name whenever the UI language changes,
// so the label always matches the rest of the UI. No-op outside live mode.
watch(locale, () => detectCurrentCity())

// Backfill English labels for any places already saved in localStorage (created
// before this feature existed) so they render correctly in the English UI.
backfillSavedEnglish()
